import type React from "react";
import { useEffect, useRef } from "react";

// Functional / Categorical: gauge zone colors encode whether the value is in
// the safe / warning / out-of-bounds band. Must stay stable across user themes.
const GAUGE = {
	IN_RANGE: "rgb(50,200,50)",
	OUT_OF_RANGE: "rgb(240, 20, 20)",
	INACTIVE: "rgb(219, 219, 219)",
	GUIDE: "lightgrey",
	NEEDLE: "black",
} as const;

interface HalfCircleProps {
	value: number; // Value between -90 and 90
	size: number; // Size of the component in pixels
}

const HalfCircle: React.FC<HalfCircleProps> = ({ value, size }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const centerX = size / 2;
		const centerY = size / 2;
		const radius = (size / 2) * 0.8; // 80% of half the size

		// Clear canvas
		ctx.clearRect(0, 0, size, size);

		// Rotate the context
		ctx.translate(centerX, centerY);
		ctx.rotate(-Math.PI / 2); // Rotate 90 degrees clockwise
		ctx.translate(-centerX, -centerY);

		// Draw the three arcs
		const splitAngle = 30;
		const arcAngles = [-90, -splitAngle, splitAngle, 90];
		arcAngles.forEach((endAngle, index) => {
			if (index === 0) return;

			const startAngle = arcAngles[index - 1];
			ctx.beginPath();
			ctx.arc(centerX, centerY, radius, (startAngle * Math.PI) / 180, (endAngle * Math.PI) / 180);

			if (value >= startAngle && value < endAngle) {
				if (value < 30 && value > -30) ctx.strokeStyle = GAUGE.IN_RANGE;
				else ctx.strokeStyle = GAUGE.OUT_OF_RANGE;
			} else {
				ctx.strokeStyle = GAUGE.INACTIVE;
			}

			ctx.lineWidth = 10;
			ctx.stroke();
		});

		// Draw dotted lines at -30 and 30 degrees
		[-splitAngle, splitAngle].forEach((angle) => {
			const endX = centerX + radius * Math.cos((angle * Math.PI) / 180);
			const endY = centerY + radius * Math.sin((angle * Math.PI) / 180);

			ctx.beginPath();
			ctx.setLineDash([5, 5]);
			ctx.moveTo(centerX, centerY);
			ctx.lineTo(endX, endY);
			ctx.strokeStyle = GAUGE.GUIDE;
			ctx.lineWidth = 2;
			ctx.stroke();
			ctx.setLineDash([]);
		});

		// Draw the value line
		const valueRadians = (value * Math.PI) / 180;
		const endX = centerX + radius * Math.cos(valueRadians);
		const endY = centerY + radius * Math.sin(valueRadians);

		ctx.beginPath();
		ctx.moveTo(centerX, centerY);
		ctx.lineTo(endX, endY);
		ctx.strokeStyle = GAUGE.NEEDLE;
		ctx.lineWidth = 3;
		ctx.stroke();

		// Reset the rotation
		ctx.setTransform(1, 0, 0, 1, 0, 0);

		// Draw the center dot
		ctx.beginPath();
		ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI);
		ctx.fillStyle = GAUGE.NEEDLE;
		ctx.fill();
	}, [value, size]);

	return <canvas ref={canvasRef} width={size} height={size} />;
};

export default HalfCircle;
